// Libs
import classNames from 'classnames/bind';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm, UseFormReturn } from 'react-hook-form';
import {
  createPurchaseOrder,
  getDetailPurchaseOrder,
  updatePurchaseOrder,
} from '~/thunks/purchaseOrder/purchaseOrderThunk';
import { Avatar, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack } from '@mui/material';
// Components, Layouts, Pages
import { BaseLabel, Loading } from '~/components/common';
import { PurchaseOrderInfoForm, PurchaseOrderProductInfoForm } from '~/components/module/purchaseOrder';
// Others
import { MethodEnum } from '~/utils/enum/common';
import { IBodyUpdate } from '~/utils/interface/common';
import { EMPTY_STRING } from '~/utils/constants/common';
import { vendorActions } from '~/thunks/vendors/vendorsSlice';
import { useAppDispatch } from '~/utils/redux/hooks';
import { dataDefaultItemProductPurchaseOrder } from '~/utils/mockData/purchaseOrder';
import { defaultValuesPurchaseOrderForm, schema } from './helper';
import { compareDataUpdate, getFullName, removeEmptyObjects } from '~/utils/helper/common';
import { IFormPurchaseOrder, IPurchaseOrderItem, IPurchaseOrderItemDetail } from '~/utils/interface/purchaseOrder';
// Styles, images, icons
import styles from './PurchaseOrderFormModal.module.scss';
import { icons } from '~/assets';

type Props = {
  type?: string;
  overbookedItemSelected?: IPurchaseOrderItemDetail;
  vendorId?: string;
  idPurchaseOrder?: string;
  quoteId?: string;
  orderId?: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const PurchaseOrderFormModal = (props: Props) => {
  //#region Destructuring Props
  const { type, idPurchaseOrder, overbookedItemSelected, vendorId, quoteId, orderId, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/purchaseOrder', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    setValue,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<IFormPurchaseOrder>({
    resolver: yupResolver(schema(t)),
    defaultValues: {
      ...defaultValuesPurchaseOrderForm,
      purchaseOrderItems: idPurchaseOrder ? [] : [dataDefaultItemProductPurchaseOrder],
    },
  });

  const { update } = useFieldArray({
    control,
    name: 'purchaseOrderItems',
  });

  useEffect(() => {
    if (!overbookedItemSelected) return;

    const newProductItem: IPurchaseOrderItem = {
      inventoryId: overbookedItemSelected?.inventoryId,
      variantId: overbookedItemSelected?.variantId,
      unitPrice: overbookedItemSelected?.rentalPrice?.toString(),
      quantity: overbookedItemSelected?.quantity?.toString(),
      total: (overbookedItemSelected?.rentalPrice || 0 * overbookedItemSelected?.quantity || 0).toFixed(2).toString(),
      idRemove: `${overbookedItemSelected?.inventoryId}_${overbookedItemSelected?.variantId}`,
    };

    update(0, newProductItem);
  }, [overbookedItemSelected]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [initialData, setInitialData] = useState<IFormPurchaseOrder>(defaultValuesPurchaseOrderForm);
  const [vendorNameDefault, setVendorNameDefault] = useState<string>(EMPTY_STRING);
  const [approvedByNameDefault, setApprovedByNameDefault] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!idPurchaseOrder) return;

    handleGetDetailPurchaseOrder(idPurchaseOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPurchaseOrder]);

  useEffect(() => {
    if (!type) return;

    setValue('type', type);
  }, [type]);

  useEffect(() => {
    if (!vendorId) return;

    setValue('vendorId', vendorId);
  }, [vendorId]);

  useEffect(() => {
    if (!quoteId) return;

    setValue('quoteId', quoteId);
  }, [quoteId]);

  useEffect(() => {
    if (!orderId) return;

    setValue('orderId', orderId);
  }, [orderId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailPurchaseOrder = (idPurchaseOrder: string) => {
    setIsLoading(true);

    const queryDetailForm = {
      id: idPurchaseOrder,
      params: { method: MethodEnum.UPDATE },
    };

    dispatch(getDetailPurchaseOrder(queryDetailForm))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, ...remainingData } = res?.data;

        const body: IFormPurchaseOrder = {
          vendorId: remainingData?.vendor.id || EMPTY_STRING,
          approvedBy: remainingData?.approvedBy?.id || EMPTY_STRING,
          approveImmediately: remainingData?.approveImmediately || false,
          type: remainingData?.type || EMPTY_STRING,
          poNumber: remainingData?.poNumber || EMPTY_STRING,
          neededByDate: remainingData?.neededByDate || EMPTY_STRING,
          deliveryDate: remainingData?.deliveryDate || EMPTY_STRING,
          pickupDate: remainingData?.pickupDate || EMPTY_STRING,
          method: remainingData?.method || EMPTY_STRING,
          pickupType: remainingData?.pickupType || EMPTY_STRING,
          deliveryTo: remainingData?.deliveryTo || EMPTY_STRING,
          shippingPrice: remainingData?.shippingPrice || EMPTY_STRING,
          note: remainingData?.note || EMPTY_STRING,
          purchaseOrderItems:
            remainingData?.purchaseOrderItems?.map((item) => ({
              id: item?.id || EMPTY_STRING,
              inventoryId: item?.inventoryId || EMPTY_STRING,
              variantId: item?.variantId || EMPTY_STRING,
              name: item?.name || EMPTY_STRING,
              unitPrice: item?.unitPrice || EMPTY_STRING,
              quantity: item?.quantity || EMPTY_STRING,
              total: item?.total || EMPTY_STRING,
              idRemove: `${item.inventoryId}_${item.variantId}`,
            })) || [],
        };

        setVendorNameDefault(remainingData?.vendor?.vendorName);
        setApprovedByNameDefault(
          getFullName({
            firstName: remainingData?.approvedBy?.firstName,
            lastName: remainingData?.approvedBy?.lastName,
          })
        );

        setInitialData(body);

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFormPurchaseOrder = (data: IFormPurchaseOrder) => {
    if (idPurchaseOrder) {
      handleEditPurchaseOrder(data);
    } else {
      handleAddPurchaseOrder(data);
    }

    setIsLoading(true);
  };

  const handleAddPurchaseOrder = (data: IFormPurchaseOrder) => {
    const convertData = removeEmptyObjects({
      ...data,
      purchaseOrderItems: data.purchaseOrderItems?.map(({ id, idRemove, name, variantName, ...rest }) => rest),
    }) as IFormPurchaseOrder;

    dispatch(createPurchaseOrder(convertData))
      .unwrap()
      .then((_res) => {
        handleCloseModal();
        dispatch(vendorActions.setIsRefreshDetail(true));
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleEditPurchaseOrder = async (data: IFormPurchaseOrder) => {
    const { purchaseOrderItems, ...res } = data;

    const dataChange = compareDataUpdate(initialData, res) as IFormPurchaseOrder;

    const convertData: IFormPurchaseOrder = {
      ...dataChange,
      ...(purchaseOrderItems && {
        purchaseOrderItems: purchaseOrderItems.map(({ id, name, idRemove, variantId, ...rest }) => {
          const base = {
            ...rest,
            quantity: Number(rest.quantity),
            unitPrice: Number(rest.unitPrice),
            total: Number(rest.total),
            ...(variantId ? { variantId: variantId } : {}),
          } as any;
          return id && id.length > 15 ? base : { id, ...base };
        }),
      }),
    };

    const params: IBodyUpdate<IFormPurchaseOrder> = {
      id: idPurchaseOrder || EMPTY_STRING,
      body: convertData,
    };

    dispatch(updatePurchaseOrder(params))
      .unwrap()
      .then(() => {
        handleCloseModal();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='purchaseOrderFormModalComponent'
      aria-labelledby='purchaseOrderFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='purchaseOrderFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel
              label={t(
                idPurchaseOrder
                  ? 'purchase_order_form_title_edit_purchase_order'
                  : 'purchase_order_form_title_add_purchase_order'
              )}
              styleType='bigTitle'
            />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <FormProvider
        {...({
          control,
          watch,
          reset,
          setValue,
          formState: { errors, isDirty },
        } as UseFormReturn<IFormPurchaseOrder>)}
      >
        <DialogContent className={cx('content')} dividers>
          <PurchaseOrderInfoForm vendorNameDefault={vendorNameDefault} approvedByNameDefault={approvedByNameDefault} />

          <PurchaseOrderProductInfoForm multiple={true} />
        </DialogContent>
      </FormProvider>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          onClick={handleSubmit(handleFormPurchaseOrder)}
          disabled={idPurchaseOrder ? !isDirty : false}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default PurchaseOrderFormModal;
